import {
    useLayoutEffect,
    useEffect,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IDirectionRootProps from "./type";
import type {
    DirectionRootWebPropsType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import type {
    DirectionType
} from "../../../../types";
import {
    isLangDirectionConflict,
    syncDocumentDirection,
    syncNativeDirection
} from "../../../../helpers/direction";
import {
    warnOnce
} from "../../../../utils/development";

const DirectionRoot: FC<IDirectionRootProps> = ({
    onDirectionChange,
    children
}) => {
    const {
        activeLocale,
        isRTL
    } = NCoreUIKitLocalize.useContext();

    const direction: DirectionType = isRTL ? "rtl" : "ltr";

    const isLangConflict = Platform.OS === "web" && isLangDirectionConflict({
        lang: activeLocale,
        direction
    });

    useLayoutEffect(() => {
        syncDocumentDirection({
            lang: activeLocale,
            direction
        });
    }, [
        activeLocale,
        direction
    ]);

    useEffect(() => {
        syncNativeDirection({
            onDirectionChange,
            direction
        });
    }, [
        direction
    ]);

    useEffect(() => {
        if(!isLangConflict) {
            return;
        }

        warnOnce({
            message: `NCoreUIKit: locale "${activeLocale}" is declared ${direction} (isRTL) but its language tag reads the other way, so lang is not applied to the root element. Use a locale tag whose script direction matches isRTL.`
        });
    }, [
        isLangConflict,
        activeLocale,
        direction
    ]);

    if(Platform.OS !== "web") {
        return children;
    }

    const webProps: DirectionRootWebPropsType = {
        lang: isLangConflict ? undefined : activeLocale,
        dir: direction
    };

    return <View
        {...webProps}
        style={stylesheet.container}
    >
        {children}
    </View>;
};
export default DirectionRoot;
